import { useEffect, useRef } from "react";
import { useQuery } from "react-query";

import { EMPTY_FILTER_ID } from "constants/app.constants";

import { getWellsFromFilterIdPaginated } from "api/filter";

import { WELL_LIST_PAGE_SIZE } from "../constants";

/**
 * How long an answer nothing is reading is kept before it is thrown out.
 *
 * Every applied filter mints a new filterId, so each fetch lands under a key
 * of its own and the key the panel was reading a moment ago goes inactive
 * rather than being replaced. Fetching while the panel is hidden means more of
 * those keys, so the bound is written down rather than left to react-query's
 * default: five minutes is long enough to come straight back to a filter
 * without refetching, and short enough that a session's worth of superseded
 * answers cannot pile up.
 */
export const WELL_LIST_CACHE_TIME_MS = 5 * 60 * 1000;

/** react-query key root for the well list; the full key is [WELL_LIST_QUERY_KEY, filterId, txnId]. */
export const WELL_LIST_QUERY_KEY = "getWellsFromFilterId";

export default function useGetWellsFromFilterId(
  filterId: string,
  txnId: string,
  handlePostData: (postData) => void,
  handleError: (err) => void = null
) {
  const { isLoading, isError, error, data, refetch } = useQuery(
    [WELL_LIST_QUERY_KEY, filterId, txnId],
    async () => {
      const result = await getWellsFromFilterIdPaginated(
        filterId,
        0,
        WELL_LIST_PAGE_SIZE
      );
      return result;
    },
    {
      // Fetches even while the panel is hidden, so a filter applied from
      // another panel is already on screen when this one is revealed. The
      // placeholder id from before the first real filter has nothing to load.
      enabled: !!filterId && filterId !== EMPTY_FILTER_ID,
      // A filterId names one set of wells and is never reused, so what is
      // fetched under it can never go stale — but it can be superseded, and
      // what nothing is reading has to be collected.
      staleTime: Infinity,
      cacheTime: WELL_LIST_CACHE_TIME_MS,
      onError: (err) => {
        if (handleError) {
          handleError(err);
        } else {
          // eslint-disable-next-line no-console
          console.error(err);
        }
      }
    }
  );

  // handlePostData is often a non-memoized inline callback; calling it via a ref
  // (instead of listing it as a dependency) keeps the delivery effect from
  // re-firing every time the caller re-renders.
  const handlePostDataRef = useRef(handlePostData);
  useEffect(() => {
    handlePostDataRef.current = handlePostData;
  }, [handlePostData]);

  // Deliver on the data, not on the fetch. react-query only fires onSuccess when
  // a request actually resolves, so a key whose answer is already cached — the
  // Network Table toggling back to a kind it has loaded before — delivered
  // nothing and left the caller holding the other kind's list.
  useEffect(() => {
    if (data === undefined) return;
    handlePostDataRef.current?.(data);
  }, [data]);

  return { isLoading, isError, error, data, refetch };
}
